// The game over the pipeline: points for every touch, a daily goal, a
// streak, levels, badges and a scoreboard. Everything is computed from the
// sales_touches rows already in the database. Nothing here is stored, so
// changing a number below rescores history on the next load. Pure: no
// React, no Supabase.
//
// Why activity is scored and not just wins: a rep controls how many calls
// and visits happen today, not who says yes. The big bonuses sit on the
// stage moves (demo, pilot, customer) so the activity points lead somewhere.
import { CADENCE, CALLBACK_KEPT_NOTE, CALLBACK_LEAD_MS, TEMP_BY_OUTCOME, isCallback, isGoingCold, tempOf, tierOf } from './salesPlan';

export const DAILY_GOAL = 50; // points a rep aims for every working day
export const STREAK_MIN = 20; // points in a day that keep the streak alive
export const WEEK_START = 1; // Monday
export const MIN_MONTHLY = 299; // the lowest Exitium price, for "at least $X a month"

// Points by what was done.
export const BASE_POINTS = { call: 1, email: 2, dm: 2, text: 2, visit: 5, demo: 5, note: 0, stage: 0, assign: 0 };
// Extra for how it went.
export const OUTCOME_POINTS = { spoke: 2, not_now: 2, callback: 3, restart: 5, referrals: 15, showed: 5, booked: 5 };
// Circling back: calling when you promised, and touching a hot store again
// inside a day. These are what turn a warm lead into a demo.
export const CALLBACK_KEPT_POINTS = 5;
export const HOT_FOLLOWUP_POINTS = 3;
export const HOT_FOLLOWUP_HOURS = 24;
// Outcomes that mean nothing was actually done.
export const SKIPPED_OUTCOMES = new Set(['no_email', 'no_account', 'no_number', 'too_far', 'bounced', 'complained', 'delivered']);
// Paid once per store per stage, the first time the store gets there.
export const STAGE_BONUS = { demo: 25, pilot: 100, customer: 250 };
// A touch that carries a real note (what they said) earns one more. The
// notes are what the brief, the objection sheet and the next rep run on.
export const NOTE_BONUS = 1;
export const NOTE_MIN_CHARS = 20;
export const hasRealNote = (t) => typeof t?.note === 'string' && t.note.trim().length >= NOTE_MIN_CHARS;
export const SEND_KINDS = new Set(['email', 'dm', 'text']);

export const LEVELS = [
  { level: 1, title: 'Rookie', xp: 0 },
  { level: 2, title: 'Dialer', xp: 100 },
  { level: 3, title: 'Door Knocker', xp: 300 },
  { level: 4, title: 'Regular', xp: 600 },
  { level: 5, title: 'Closer', xp: 1000 },
  { level: 6, title: 'Rainmaker', xp: 1600 },
  { level: 7, title: 'Territory Boss', xp: 2500 },
  { level: 8, title: 'Legend', xp: 4000 },
  { level: 9, title: 'Hall of Fame', xp: 6500 },
  { level: 10, title: 'Founder Status', xp: 10000 },
];

export const BADGES = [
  { key: 'first_call', name: 'First dial', hint: 'Log your first call.', icon: 'phone' },
  { key: 'early_bird', name: 'Early bird', hint: 'Log a touch before 9 in the morning.', icon: 'sunrise' },
  { key: 'hot_phone', name: 'Hot phone', hint: '20 calls in one day.', icon: 'flame' },
  { key: 'phone_warrior', name: 'Phone warrior', hint: '50 calls in one week.', icon: 'swords' },
  { key: 'century', name: 'Century', hint: '100 calls, all time.', icon: 'medal' },
  { key: 'boots_on', name: 'Boots on', hint: 'Your first walk-in.', icon: 'footprints' },
  { key: 'road_warrior', name: 'Road warrior', hint: '5 walk-ins in one day.', icon: 'car' },
  { key: 'full_sequence', name: 'Full sequence', hint: 'Take one store through all seven steps.', icon: 'route' },
  { key: 'showtime', name: 'Showtime', hint: 'Your first demo.', icon: 'tv' },
  { key: 'hat_trick', name: 'Hat trick', hint: '3 demos in one week.', icon: 'sparkles' },
  { key: 'pilot', name: 'Pilot', hint: 'Your first pilot signed.', icon: 'rocket' },
  { key: 'closer', name: 'Closer', hint: 'Your first paying customer.', icon: 'trophy' },
  { key: 'word_of_mouth', name: 'Word of mouth', hint: 'Get a referral from a pilot or customer.', icon: 'megaphone' },
  { key: 'scribe', name: 'Scribe', hint: '25 touches with a real note of what they said.', icon: 'pen' },
  { key: 'prepared', name: 'Prepared', hint: 'Build the brief on 10 stores before calling.', icon: 'search' },
  { key: 'promise_keeper', name: 'Promise keeper', hint: 'Call back on time 10 times.', icon: 'calendar' },
  { key: 'heat', name: 'Keep it hot', hint: 'Touch a hot store again within a day, 10 times.', icon: 'flame' },
  { key: 'reminder', name: 'On the calendar', hint: 'Send 5 demo reminders. Reminders cut no-shows by a quarter.', icon: 'calendar' },
  { key: 'goal_getter', name: 'Goal getter', hint: `Hit the ${DAILY_GOAL}-point daily goal.`, icon: 'target' },
  { key: 'big_week', name: 'Big week', hint: '300 points in one week.', icon: 'zap' },
  { key: 'iron_streak', name: 'Iron streak', hint: 'A 5-day streak.', icon: 'flame' },
  { key: 'unbreakable', name: 'Unbreakable', hint: 'A 20-day streak.', icon: 'shield' },
];

// ---------- dates ----------
const pad = (n) => String(n).padStart(2, '0');
export const dayKey = (d) => {
  const x = new Date(d);
  return `${x.getFullYear()}-${pad(x.getMonth() + 1)}-${pad(x.getDate())}`;
};
export const startOfDay = (d) => { const x = new Date(d); x.setHours(0, 0, 0, 0); return x; };
export const startOfWeek = (d) => {
  const x = startOfDay(d);
  x.setDate(x.getDate() - ((x.getDay() - WEEK_START + 7) % 7));
  return x;
};
export const startOfMonth = (d) => { const x = startOfDay(d); x.setDate(1); return x; };
export const isWeekend = (d) => { const g = new Date(d).getDay(); return g === 0 || g === 6; };
const addDays = (d, n) => { const x = new Date(d); x.setDate(x.getDate() + n); return x; };

// ---------- scoring ----------
/**
 * Score every touch. Returns new objects with `.points` and `.bonusStage`
 * (the stage whose bonus this touch earned, if any), oldest first. Stage
 * bonuses are paid once per store per stage, whichever rep's touch got it
 * there first, so moving a store back and forth earns nothing extra.
 */
export function scoreTouches(touches) {
  const sorted = [...touches].sort((a, b) => new Date(a.happened_at) - new Date(b.happened_at));
  const paid = new Set();
  const lastHot = new Map(); // prospect -> time of the last touch that left it hot
  return sorted.map((t) => {
    let points = 0;
    let bonusStage = null;
    let kept = false;
    let heat = false;
    const at = new Date(t.happened_at).getTime();
    const work = !SKIPPED_OUTCOMES.has(t.outcome) && t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign';
    if (!SKIPPED_OUTCOMES.has(t.outcome)) {
      points += BASE_POINTS[t.kind] || 0;
      points += OUTCOME_POINTS[t.outcome] || 0;
      if (hasRealNote(t) && t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign') points += NOTE_BONUS;
    }
    if (work && typeof t.note === 'string' && t.note.startsWith(CALLBACK_KEPT_NOTE)) { points += CALLBACK_KEPT_POINTS; kept = true; }
    const hotAt = lastHot.get(t.prospect_id);
    if (work && hotAt != null && at - hotAt <= HOT_FOLLOWUP_HOURS * 3600000) { points += HOT_FOLLOWUP_POINTS; heat = true; }
    if (work || t.outcome === 'reply_received') {
      if (TEMP_BY_OUTCOME[t.outcome] === 'hot' || t.outcome === 'reply_received') lastHot.set(t.prospect_id, at);
      else if (TEMP_BY_OUTCOME[t.outcome]) lastHot.delete(t.prospect_id);
    }
    if (t.to_stage && t.to_stage !== t.from_stage && STAGE_BONUS[t.to_stage]) {
      const k = `${t.prospect_id}:${t.to_stage}`;
      if (!paid.has(k)) {
        paid.add(k);
        points += STAGE_BONUS[t.to_stage];
        bonusStage = t.to_stage;
      }
    }
    return { ...t, points, bonusStage, kept, heat };
  });
}

export function levelFor(xp) {
  let cur = LEVELS[0];
  for (const l of LEVELS) if (xp >= l.xp) cur = l;
  const next = LEVELS.find((l) => l.xp > cur.xp) || null;
  const span = next ? next.xp - cur.xp : 1;
  return { ...cur, xpInto: xp - cur.xp, xpToNext: next ? next.xp - xp : 0, nextXp: next?.xp ?? null, nextTitle: next?.title ?? null, progress: next ? Math.min(1, (xp - cur.xp) / span) : 1 };
}

const sumBy = (rows, f) => rows.reduce((n, r) => n + f(r), 0);
const tally = (rows) => ({
  points: sumBy(rows, (t) => t.points),
  touches: rows.filter((t) => t.points > 0 || (t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign' && !SKIPPED_OUTCOMES.has(t.outcome))).length,
  calls: rows.filter((t) => t.kind === 'call' && !SKIPPED_OUTCOMES.has(t.outcome)).length,
  sends: rows.filter((t) => SEND_KINDS.has(t.kind) && !SKIPPED_OUTCOMES.has(t.outcome)).length,
  visits: rows.filter((t) => t.kind === 'visit').length,
  conversations: rows.filter((t) => t.kind === 'call' && (t.outcome === 'spoke' || t.outcome === 'not_now' || t.outcome === 'booked' || t.outcome === 'pilot')).length,
  demos: rows.filter((t) => t.bonusStage === 'demo').length,
  pilots: rows.filter((t) => t.bonusStage === 'pilot').length,
  customers: rows.filter((t) => t.bonusStage === 'customer').length,
});

/** Points per local day for a rep's scored touches. */
export function pointsByDay(rows) {
  const m = new Map();
  for (const t of rows) {
    const k = dayKey(t.happened_at);
    m.set(k, (m.get(k) || 0) + t.points);
  }
  return m;
}

/**
 * The streak: consecutive working days with at least STREAK_MIN points.
 * A weekend with nothing logged does not break it; a weekend with work
 * counts as a day. Today only counts once it qualifies, so a streak is
 * "alive" through today until the rep misses a working day.
 */
export function streakFor(byDay, now = new Date()) {
  const q = (d) => (byDay.get(dayKey(d)) || 0) >= STREAK_MIN;
  const todayDone = q(now);
  let count = 0;
  let cur = todayDone ? startOfDay(now) : addDays(startOfDay(now), -1);
  for (let guard = 0; guard < 4000; guard++) {
    if (q(cur)) { count += 1; cur = addDays(cur, -1); continue; }
    if (isWeekend(cur)) { cur = addDays(cur, -1); continue; }
    break;
  }
  return { count, todayDone, atRisk: count > 0 && !todayDone && !isWeekend(now) };
}

/** The longest streak ever, same rules, for the badges. */
export function bestStreak(byDay, now = new Date()) {
  const keys = [...byDay.keys()].sort();
  if (!keys.length) return 0;
  let best = 0;
  let run = 0;
  let cur = startOfDay(new Date(keys[0] + 'T12:00:00'));
  const end = startOfDay(now);
  for (let guard = 0; cur <= end && guard < 4000; guard++) {
    const pts = byDay.get(dayKey(cur)) || 0;
    if (pts >= STREAK_MIN) { run += 1; best = Math.max(best, run); }
    else if (!isWeekend(cur)) run = 0;
    cur = addDays(cur, 1);
  }
  return best;
}

const weekKey = (iso) => dayKey(startOfWeek(new Date(iso)));
const countBy = (rows, keyFn) => {
  const m = new Map();
  for (const r of rows) m.set(keyFn(r), (m.get(keyFn(r)) || 0) + 1);
  return Math.max(0, ...m.values());
};

/** Which badges a rep has earned, from their scored touches. Returns a Set of keys. */
export function badgesFor(rows, { byDay, best }) {
  const got = new Set();
  const calls = rows.filter((t) => t.kind === 'call' && !SKIPPED_OUTCOMES.has(t.outcome));
  const visits = rows.filter((t) => t.kind === 'visit');
  const demos = rows.filter((t) => t.bonusStage === 'demo');
  if (calls.length) got.add('first_call');
  if (rows.some((t) => new Date(t.happened_at).getHours() < 9)) got.add('early_bird');
  if (countBy(calls, (t) => dayKey(t.happened_at)) >= 20) got.add('hot_phone');
  if (countBy(calls, (t) => weekKey(t.happened_at)) >= 50) got.add('phone_warrior');
  if (calls.length >= 100) got.add('century');
  if (visits.length) got.add('boots_on');
  if (countBy(visits, (t) => dayKey(t.happened_at)) >= 5) got.add('road_warrior');
  if (rows.some((t) => t.step === CADENCE.length - 1)) got.add('full_sequence');
  if (demos.length) got.add('showtime');
  if (countBy(demos, (t) => weekKey(t.happened_at)) >= 3) got.add('hat_trick');
  if (rows.some((t) => t.bonusStage === 'pilot')) got.add('pilot');
  if (rows.some((t) => t.bonusStage === 'customer')) got.add('closer');
  if (rows.some((t) => t.outcome === 'referrals')) got.add('word_of_mouth');
  if ([...byDay.values()].some((p) => p >= DAILY_GOAL)) got.add('goal_getter');
  const byWeek = new Map();
  for (const t of rows) { const k = weekKey(t.happened_at); byWeek.set(k, (byWeek.get(k) || 0) + t.points); }
  if ([...byWeek.values()].some((p) => p >= 300)) got.add('big_week');
  if (rows.filter((t) => hasRealNote(t) && t.kind !== 'note' && t.kind !== 'stage' && t.kind !== 'assign').length >= 25) got.add('scribe');
  if (rows.filter((t) => t.kind === 'note' && t.outcome === 'brief').length >= 10) got.add('prepared');
  if (rows.filter((t) => t.outcome === 'reminded').length >= 5) got.add('reminder');
  if (rows.filter((t) => t.kept).length >= 10) got.add('promise_keeper');
  if (rows.filter((t) => t.heat).length >= 10) got.add('heat');
  if (best >= 5) got.add('iron_streak');
  if (best >= 20) got.add('unbreakable');
  return got;
}

/**
 * Everything the screen needs about one rep.
 * rows = scored touches (all reps); the rep's own are picked out here.
 */
export function repStats(scored, repId, now = new Date()) {
  const mine = scored.filter((t) => t.rep_id === repId);
  const d0 = startOfDay(now).getTime();
  const w0 = startOfWeek(now).getTime();
  const m0 = startOfMonth(now).getTime();
  const at = (t) => new Date(t.happened_at).getTime();
  const today = tally(mine.filter((t) => at(t) >= d0));
  const week = tally(mine.filter((t) => at(t) >= w0));
  const month = tally(mine.filter((t) => at(t) >= m0));
  const all = tally(mine);
  const byDay = pointsByDay(mine);
  const streak = streakFor(byDay, now);
  const best = Math.max(bestStreak(byDay, now), streak.count);
  const last7 = [];
  for (let i = 6; i >= 0; i--) {
    const d = addDays(startOfDay(now), -i);
    last7.push({ key: dayKey(d), label: d.toLocaleDateString(undefined, { weekday: 'narrow' }), points: byDay.get(dayKey(d)) || 0, isToday: i === 0 });
  }
  return { repId, today, week, month, all, level: levelFor(all.points), streak, bestStreak: best, last7, badges: badgesFor(mine, { byDay, best }), goal: DAILY_GOAL, goalHit: today.points >= DAILY_GOAL };
}

/** Scoreboard rows for a period: 'today' | 'week' | 'month' | 'all'. Leader first. */
export function scoreboard(scored, reps, period = 'week', now = new Date()) {
  const from = period === 'today' ? startOfDay(now) : period === 'week' ? startOfWeek(now) : period === 'month' ? startOfMonth(now) : new Date(0);
  const f = from.getTime();
  const rows = reps.map((r) => ({ rep: r, ...tally(scored.filter((t) => t.rep_id === r.id && new Date(t.happened_at).getTime() >= f)) }));
  rows.sort((a, b) => b.points - a.points || b.touches - a.touches || a.rep.name.localeCompare(b.rep.name));
  return rows;
}

/** Team totals for a period. */
export function teamTally(scored, period = 'today', now = new Date()) {
  const from = period === 'today' ? startOfDay(now) : period === 'week' ? startOfWeek(now) : period === 'month' ? startOfMonth(now) : new Date(0);
  const f = from.getTime();
  return tally(scored.filter((t) => new Date(t.happened_at).getTime() >= f));
}

// ---------- the run queue ----------
export const RUN_KINDS = [
  { id: 'all', label: 'Everything' },
  { id: 'call', label: 'Calls' },
  { id: 'send', label: 'Email and messages' },
  { id: 'visit', label: 'Walk-ins' },
  { id: 'ask', label: 'Demos and pilots' },
];

const kindOf = (action) => {
  if (!action) return null;
  if (action.isReply) return 'send';
  if (action.isSequence) return action.kind === 'call' ? 'call' : SEND_KINDS.has(action.kind) ? 'send' : action.kind === 'visit' ? 'visit' : 'send';
  return 'ask';
};

/** 0 to 3: website, email, social. Stores already online are the readier buyers. */
export const digitalFit = (p) => (p?.website ? 1 : 0) + (p?.email ? 1 : 0) + (p?.social ? 1 : 0);

/**
 * What a rep should work right now: stores due today or overdue, owned by
 * the rep or by nobody yet, oldest due first. `skipped` ids sort last.
 * `actionOf` is salesPlan.currentAction; `bucketOf` is salesPlan.dueBucket.
 */
export function buildQueue(prospects, { me, kind = 'all', skipped = new Set(), actionOf, bucketOf, now = new Date() }) {
  const out = [];
  for (const p of prospects) {
    if (p.stage === 'lost' || !p.next_due_at) continue;
    if (me && p.rep_id && p.rep_id !== me.id) continue;
    if (!me && p.rep_id) continue;
    const cold = isGoingCold(p, now);
    const due = new Date(p.next_due_at).getTime();
    const cb = isCallback(p);
    // A promised call-back waits for its minute; a hot store left alone comes back early.
    if (cb && due - CALLBACK_LEAD_MS > now.getTime() && !cold) continue;
    const b = bucketOf(p.next_due_at, now);
    if (b !== 'overdue' && b !== 'today' && !cold) continue;
    const a = actionOf(p);
    if (!a) continue;
    const k = kindOf(a);
    if (kind !== 'all' && k !== kind) continue;
    const dueDay = new Date(p.next_due_at); dueDay.setHours(0, 0, 0, 0);
    const temp = tempOf(p);
    const urgency = a.isReply ? 0 : cb ? 1 : cold ? 2 : temp === 'hot' ? 3 : temp === 'warm' ? 4 : 5;
    const tier = { A: 0, B: 1, C: 2 }[tierOf(p)] ?? 1;
    out.push({ p, a, k, due, dueDay: Math.min(dueDay.getTime(), now.getTime()), fit: digitalFit(p), skip: skipped.has(p.id) ? 1 : 0, mine: p.rep_id ? 0 : 1, urgency, tier });
  }
  // Replies, then call-backs whose time has come, then hot stores going cold,
  // then hot, warm, the rest. Inside that: mine before the pool, overdue by
  // day, tier A before B before C, the readier store, the earlier time.
  out.sort((x, y) => x.skip - y.skip || x.urgency - y.urgency || x.mine - y.mine || x.dueDay - y.dueDay || x.tier - y.tier || y.fit - x.fit || x.due - y.due || (x.p.city || '').localeCompare(y.p.city || ''));
  return out.map((x) => x.p);
}

export const money = (n) => `$${Math.round(n).toLocaleString()}`;

// ---------- sprints ----------
// A sprint is a short contest on one number. Standings are the tally of
// each rep's scored touches inside the window, nothing stored.
export const SPRINT_METRICS = [
  { id: 'calls', label: 'calls', noun: 'call' },
  { id: 'sends', label: 'sends', noun: 'send' },
  { id: 'visits', label: 'walk-ins', noun: 'walk-in' },
  { id: 'demos', label: 'demos', noun: 'demo' },
  { id: 'points', label: 'points', noun: 'point' },
];
export const SPRINT_METRIC_BY_ID = Object.fromEntries(SPRINT_METRICS.map((m) => [m.id, m]));

/** Ready-made sprints. `until` is minutes from now, or a clock hour today, or 'day' | 'week'. */
export const SPRINT_PRESETS = [
  { key: 'power_hour', name: 'Power hour', metric: 'calls', minutes: 60, hint: 'Most calls in the next 60 minutes.' },
  { key: 'blitz', name: 'Thirty-minute blitz', metric: 'calls', minutes: 30, hint: 'Most calls in the next 30 minutes.' },
  { key: 'send_off', name: 'Send-off', metric: 'sends', untilHour: 17, hint: 'Most emails, DMs and texts by 5pm today.' },
  { key: 'day', name: 'Day of dials', metric: 'calls', until: 'day', hint: 'Most calls by the end of today.' },
  { key: 'boots', name: 'Boots week', metric: 'visits', until: 'week', hint: 'Most walk-ins by the end of the week.' },
  { key: 'demo_week', name: 'Demo week', metric: 'demos', until: 'week', hint: 'Most demos by the end of the week.' },
];

/** The window a preset means, from now. */
export function sprintWindow(preset, now = new Date()) {
  const start = new Date(now);
  let end;
  if (preset.minutes) end = new Date(start.getTime() + preset.minutes * 60000);
  else if (preset.untilHour != null) { end = new Date(start); end.setHours(preset.untilHour, 0, 0, 0); if (end <= start) end.setDate(end.getDate() + 1); }
  else if (preset.until === 'week') { end = startOfWeek(start); end.setDate(end.getDate() + 7); }
  else { end = startOfDay(start); end.setDate(end.getDate() + 1); }
  return { starts_at: start.toISOString(), ends_at: end.toISOString() };
}

/** 'upcoming' | 'live' | 'done', with the milliseconds left. */
export function sprintStatus(sprint, now = new Date()) {
  const t = now.getTime();
  const s = new Date(sprint.starts_at).getTime();
  const e = new Date(sprint.ends_at).getTime();
  if (t < s) return { status: 'upcoming', msLeft: s - t };
  if (t < e) return { status: 'live', msLeft: e - t };
  return { status: 'done', msLeft: 0 };
}

/** Standings for a sprint: one row per rep, leader first, with the metric's number. */
export function sprintStandings(scored, reps, sprint) {
  const s = new Date(sprint.starts_at).getTime();
  const e = new Date(sprint.ends_at).getTime();
  const metric = SPRINT_METRIC_BY_ID[sprint.metric] ? sprint.metric : 'calls';
  const rows = reps.filter((r) => r.active !== false).map((r) => {
    const t = tally(scored.filter((x) => x.rep_id === r.id && new Date(x.happened_at).getTime() >= s && new Date(x.happened_at).getTime() < e));
    return { rep: r, value: t[metric] || 0, points: t.points };
  });
  rows.sort((a, b) => b.value - a.value || b.points - a.points || a.rep.name.localeCompare(b.rep.name));
  return rows;
}

/** The sprint to show: the live one, else the next one coming, else the last one that ended within a day. */
export function pickSprint(sprints, now = new Date()) {
  const withStatus = sprints.map((sp) => ({ sp, ...sprintStatus(sp, now) }));
  const live = withStatus.filter((x) => x.status === 'live').sort((a, b) => a.msLeft - b.msLeft)[0];
  if (live) return live.sp;
  const next = withStatus.filter((x) => x.status === 'upcoming').sort((a, b) => a.msLeft - b.msLeft)[0];
  if (next) return next.sp;
  const dayAgo = now.getTime() - 86400000;
  const done = withStatus.filter((x) => x.status === 'done' && new Date(x.sp.ends_at).getTime() >= dayAgo).sort((a, b) => new Date(b.sp.ends_at) - new Date(a.sp.ends_at))[0];
  return done ? done.sp : null;
}

/** "42:10" or "3h 12m" or "2d 4h" */
export function countdown(ms) {
  const s = Math.max(0, Math.floor(ms / 1000));
  if (s < 3600) return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
  const h = Math.floor(s / 3600);
  if (h < 24) return `${h}h ${Math.floor((s % 3600) / 60)}m`;
  return `${Math.floor(h / 24)}d ${h % 24}h`;
}

/** The hour of the day after which an unmet streak is nagged about. */
export const NUDGE_HOUR = 14;
